import React, { useState, useEffect } from 'react';
import { whatsappAPI } from '../../services/api';
import { toast } from 'react-toastify';

const WhatsAppBroadcast = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('create');

  // Campaign creation state
  const [campaignData, setCampaignData] = useState({
    name: '',
    message: '',
    templateName: '',
    targetAudience: 'all',
    scheduledDate: '',
    scheduledTime: '',
    isScheduled: false
  });

  // Contact list state
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactFile, setContactFile] = useState(null);
  const [contactFilters, setContactFilters] = useState({
    search: '',
    contactType: '',
    isActive: true
  });
  const [contactsLoading, setContactsLoading] = useState(false);

  // Template state
  const [templates, setTemplates] = useState([]);
  const [templateData, setTemplateData] = useState({
    name: '',
    category: 'marketing',
    language: 'en',
    content: '',
    variables: []
  });

  useEffect(() => {
    loadCampaigns();
    loadTemplates();
    loadContacts();
  }, []);

  // Reload contacts when filters change
  useEffect(() => {
    if (contactFilters.search || contactFilters.contactType || contactFilters.isActive !== undefined) {
      loadContacts();
    }
  }, [contactFilters]);

  const loadCampaigns = async () => {
    try {
      // This would load campaigns from the database
      setCampaigns([
        {
          id: 1,
          name: 'Welcome Campaign',
          status: 'completed',
          sent: 150,
          delivered: 145,
          read: 120,
          createdAt: '2024-01-15'
        },
        {
          id: 2,
          name: 'Festival Sale',
          status: 'scheduled',
          sent: 0,
          delivered: 0,
          read: 0,
          scheduledDate: '2024-01-20'
        }
      ]);
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      // This would load templates from the database
      setTemplates([
        {
          id: 1,
          name: 'welcome_message',
          category: 'marketing',
          language: 'en',
          content: 'Welcome to our store! Get 10% off your first purchase with code WELCOME10.',
          status: 'approved'
        },
        {
          id: 2,
          name: 'order_update',
          category: 'transactional',
          language: 'en',
          content: 'Your order #{order_number} has been {status}. Track your order at {tracking_link}',
          status: 'pending'
        }
      ]);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const loadContacts = async () => {
    setContactsLoading(true);
    try {
      // Build query parameters for filtering
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('limit', '1000');
      
      if (contactFilters.search) {
        params.append('search', contactFilters.search);
      }
      if (contactFilters.contactType) {
        params.append('contactType', contactFilters.contactType);
      }
      if (contactFilters.isActive !== undefined) {
        params.append('isActive', contactFilters.isActive.toString());
      }
      
      // Load contacts from the WhatsApp Contacts management system
      const response = await whatsappAPI.getContacts(params.toString());
      
      if (response.success && response.data) {
        // Transform the contacts to match the broadcast format
        const transformedContacts = response.data.map(contact => ({
          id: contact.id,
          phone: contact.phone_number,
          name: contact.name,
          tags: Array.isArray(contact.tags) ? contact.tags : [],
          email: contact.email,
          company: contact.company,
          contact_type: contact.contact_type,
          is_active: contact.is_active
        }));
        
        setContacts(transformedContacts);
      } else {
        console.error('Failed to load contacts:', response);
        toast.error('Failed to load contacts');
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast.error('Error loading contacts');
    } finally {
      setContactsLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignData.name || !campaignData.message) {
      toast.error('Please fill all required fields');
      return;
    }

    // Get phone numbers based on target audience
    let targetPhoneNumbers = [];
    
    switch (campaignData.targetAudience) {
      case 'selected':
        targetPhoneNumbers = contacts.filter(contact => selectedContacts.includes(contact.id)).map(c => c.phone);
        break;
      case 'vip':
        targetPhoneNumbers = contacts.filter(contact => contact.contact_type === 'VIP').map(c => c.phone);
        break;
      case 'new':
        targetPhoneNumbers = contacts.filter(contact => contact.contact_type === 'New').map(c => c.phone);
        break;
      case 'returning':
        targetPhoneNumbers = contacts.filter(contact => contact.contact_type === 'Returning').map(c => c.phone);
        break;
      case 'premium':
        targetPhoneNumbers = contacts.filter(contact => contact.contact_type === 'Premium').map(c => c.phone);
        break;
      default: // 'all'
        targetPhoneNumbers = contacts.map(c => c.phone);
    }

    if (targetPhoneNumbers.length === 0) {
      toast.error('No contacts found for the selected target audience. Please add contacts first.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await whatsappAPI.sendBroadcast({
        phoneNumbers: targetPhoneNumbers,
        message: campaignData.message,
        templateName: campaignData.templateName || null,
        language: 'en',
        variables: []
      });
      
      if (response.data.success) {
        toast.success(`Campaign created successfully! Will be sent to ${targetPhoneNumbers.length} contacts.`);
        setCampaignData({
          name: '',
          message: '',
          templateName: '',
          targetAudience: 'all',
          scheduledDate: '',
          scheduledTime: '',
          isScheduled: false
        });
        setSelectedContacts([]);
        loadCampaigns();
      } else {
        toast.error(response.data.error || 'Failed to create campaign');
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAllContacts = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(contact => contact.id));
    }
  };

  const handleContactFilterChange = (key, value) => {
    setContactFilters(prev => ({ ...prev, [key]: value }));
  };

  const refreshContacts = () => {
    loadContacts();
  };

  const handleCreateTemplate = async () => {
    if (!templateData.name || !templateData.content) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const response = await whatsappAPI.createTemplate({
        templateName: templateData.name,
        templateLanguage: templateData.language,
        templateCategory: templateData.category,
        templateContent: templateData.content,
        templateVariables: templateData.variables
      });

      if (response.data.success) {
        toast.success('Template created successfully!');
        setTemplateData({
          name: '',
          category: 'marketing',
          language: 'en',
          content: '',
          variables: []
        });
        loadTemplates();
      } else {
        toast.error(response.data.error || 'Failed to create template');
      }
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContactUpload = async () => {
    if (!contactFile) {
      toast.error('Please select a file');
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('contacts', contactFile);

      const response = await whatsappAPI.uploadContacts(formData);

      if (response.data.success) {
        toast.success('Contacts uploaded successfully!');
        setContactFile(null);
        loadContacts();
      } else {
        toast.error(response.data.error || 'Failed to upload contacts');
      }
    } catch (error) {
      console.error('Error uploading contacts:', error);
      toast.error('Failed to upload contacts');
    } finally {
      setIsLoading(false);
    }
  };



  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
      case 'scheduled':
        return 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30';
      case 'draft':
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800';
    }
  };

  const tabs = [
    { id: 'create', label: 'Create Campaign', icon: '📢' },
    { id: 'templates', label: 'Templates', icon: '📝' },
    { id: 'contacts', label: 'Contacts', icon: '👥' },
    { id: 'campaigns', label: 'Campaigns', icon: '📊' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'create':
  return (
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">Create WhatsApp Broadcast Campaign</h3>
              <p className="text-blue-700 dark:text-blue-300 text-sm">Send marketing messages to your customer base via WhatsApp.</p>
              <p className="text-blue-600 dark:text-blue-200 text-sm mt-2">
                💡 <strong>Tip:</strong> Contacts are automatically loaded from the WhatsApp Contacts management system. 
                Go to the "Contacts" tab to filter and select specific contacts for your campaign.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Campaign Name *</label>
                  <input
                    type="text"
                    value={campaignData.name || ''}
                    onChange={(e) => setCampaignData({...campaignData, name: e.target.value})}
                    placeholder="Summer Sale Campaign"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Message Content *</label>
                  <textarea
                    value={campaignData.message || ''}
                    onChange={(e) => setCampaignData({...campaignData, message: e.target.value})}
                    placeholder="Enter your message here..."
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum 1000 characters</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Use Template (Optional)</label>
                  <select
                                          value={campaignData.templateName || ''}
                    onChange={(e) => setCampaignData({...campaignData, templateName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select a template</option>
                    {templates.filter(t => t.is_active === 1).map(template => (
                      <option key={template.id} value={template.template_name}>{template.template_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Target Audience</label>
                  <select
                    value={campaignData.targetAudience || 'all'}
                    onChange={(e) => setCampaignData({...campaignData, targetAudience: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="all">All Contacts ({contacts.length})</option>
                    <option value="selected">Selected Contacts ({selectedContacts.length})</option>
                    <option value="vip">VIP Customers ({contacts.filter(c => c.contact_type === 'VIP').length})</option>
                    <option value="new">New Customers ({contacts.filter(c => c.contact_type === 'New').length})</option>
                    <option value="returning">Returning Customers ({contacts.filter(c => c.contact_type === 'Returning').length})</option>
                    <option value="premium">Premium Customers ({contacts.filter(c => c.contact_type === 'Premium').length})</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {selectedContacts.length > 0 
                      ? `Currently selected: ${selectedContacts.length} contacts` 
                      : 'Go to Contacts tab to select specific contacts'
                    }
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                      type="checkbox"
                      checked={campaignData.isScheduled}
                      onChange={(e) => setCampaignData({...campaignData, isScheduled: e.target.checked})}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Schedule Campaign</span>
                    </label>
                  </div>

                {campaignData.isScheduled && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date</label>
                      <input
                        type="date"
                        value={campaignData.scheduledDate || ''}
                        onChange={(e) => setCampaignData({...campaignData, scheduledDate: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Time</label>
                      <input
                        type="time"
                        value={campaignData.scheduledTime || ''}
                        onChange={(e) => setCampaignData({...campaignData, scheduledTime: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                )}

                <button
                  onClick={handleCreateCampaign}
                  disabled={isLoading || !campaignData.name || !campaignData.message}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creating...' : campaignData.isScheduled ? 'Schedule Campaign' : 'Send Campaign'}
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Campaign Preview</h4>
                  <div className="bg-white dark:bg-gray-700 p-3 rounded border border-gray-200 dark:border-gray-600">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">To: {contacts.length} contacts</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{campaignData.name || 'Campaign Name'}</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">{campaignData.message || 'Your message will appear here...'}</p>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">Selected Contacts</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedContacts.length > 0 ? (
                      selectedContacts.map(contactId => {
                        const contact = contacts.find(c => c.id === contactId);
                        return contact ? (
                          <div key={contact.id} className="flex items-center justify-between text-sm">
                            <span className="text-gray-900 dark:text-white">{contact.name} ({contact.phone})</span>
                            <button
                              onClick={() => handleContactSelection(contact.id)}
                              className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Remove
                            </button>
                          </div>
                        ) : null;
                      })
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400 text-sm">No contacts selected. Will send to all contacts.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'templates':
        return (
          <div className="space-y-6">
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 dark:text-green-100 mb-2">WhatsApp Templates Management</h3>
              <p className="text-green-700 dark:text-green-300 text-sm">Create and manage approved WhatsApp message templates.</p>
                </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 dark:text-white">Create New Template</h4>
                
                  <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Template Name *</label>
                  <input
                    type="text"
                    value={(templateData?.name || '')}
                    onChange={(e) => setTemplateData({...(templateData || {}), name: e.target.value})}
                    placeholder="welcome_message"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
                      <select
                      value={(templateData?.category || 'marketing')}
                      onChange={(e) => setTemplateData({...(templateData || {}), category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="marketing">Marketing</option>
                      <option value="transactional">Transactional</option>
                      <option value="support">Support</option>
                      </select>
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Language</label>
                      <select
                      value={(templateData?.language || 'en')}
                      onChange={(e) => setTemplateData({...(templateData || {}), language: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="en">English</option>
                        <option value="hi">Hindi</option>
                        <option value="te">Telugu</option>
                      </select>
                    </div>
                  </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Template Content *</label>
                  <textarea
                    value={(templateData?.content || '')}
                    onChange={(e) => setTemplateData({...(templateData || {}), content: e.target.value})}
                    placeholder="Enter template content with variables like {name}, {order_number}..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Use {'{variable_name}'} for dynamic content</p>
                </div>

                <button
                  onClick={handleCreateTemplate}
                  disabled={isLoading || !templateData?.name || !templateData?.content}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creating...' : 'Create Template'}
                </button>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 dark:text-white">Existing Templates</h4>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {templates.map(template => (
                    <div key={template.id} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="font-medium text-gray-900 dark:text-white">{template.template_name}</h5>
                        <span className={`px-2 py-1 rounded-full text-xs ${template.is_active === 1 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'}`}>
                          {template.is_active === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{template.content}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{template.category} • {template.language}</span>
                        <span>ID: {template.id}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'contacts':
        return (
          <div className="space-y-6">
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100 mb-2">Contact Management</h3>
              <p className="text-purple-700 dark:text-purple-300 text-sm">Filter and select contacts from your WhatsApp Contacts management system for targeted campaigns.</p>
              <p className="text-purple-600 dark:text-purple-200 text-sm mt-2">
                💡 <strong>Note:</strong> To add, edit, or manage contacts, go to the "WhatsApp Contacts" section in the admin panel.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:p-6">
              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 dark:text-white">Contact Filters</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Search</label>
                    <input
                      type="text"
                      placeholder="Search by name, phone, or email..."
                      value={contactFilters.search}
                      onChange={(e) => handleContactFilterChange('search', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Contact Type</label>
                    <select
                      value={contactFilters.contactType}
                      onChange={(e) => handleContactFilterChange('contactType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="">All Types</option>
                      <option value="VIP">VIP</option>
                      <option value="Returning">Returning</option>
                      <option value="New">New</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Premium">Premium</option>
                      <option value="Regular">Regular</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                    <select
                      value={contactFilters.isActive}
                      onChange={(e) => handleContactFilterChange('isActive', e.target.value === 'true')}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="true">Active Only</option>
                      <option value="false">Inactive Only</option>
                    </select>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={loadContacts}
                      className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700"
                    >
                      Apply Filters
                    </button>
                    <button
                      onClick={refreshContacts}
                      className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700"
                    >
                      Refresh
                    </button>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <h5 className="font-medium text-gray-900 dark:text-white mb-2">Contact Statistics</h5>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Total Contacts:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">{contacts.length}</span>
                    </div>
                  <div>
                      <span className="text-gray-600 dark:text-gray-400">Selected:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-white">{selectedContacts.length}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-gray-900 dark:text-white">Contact List</h4>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedContacts.length} of {contacts.length} selected
                    </span>
                    <button
                      onClick={handleSelectAllContacts}
                      className="text-sm text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                    >
                      {selectedContacts.length === contacts.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {contactsLoading ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      Loading contacts...
                    </div>
                  ) : contacts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No contacts found. Add contacts in the WhatsApp Contacts section.
                    </div>
                  ) : (
                    contacts.map(contact => (
                      <div key={contact.id} className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedContacts.includes(contact.id)}
                            onChange={() => handleContactSelection(contact.id)}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{contact.name}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{contact.phone}</p>
                            {contact.contact_type && (
                              <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                                contact.contact_type === 'VIP' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200' :
                                contact.contact_type === 'Premium' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200' :
                                'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                              }`}>
                                {contact.contact_type}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          {contact.tags && contact.tags.length > 0 ? (
                            contact.tags.map(tag => (
                              <span key={tag} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded">
                                {tag}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-400 dark:text-gray-500">No tags</span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
            </div>
          </div>
        </div>
        );

      case 'campaigns':
        return (
        <div className="space-y-6">
            <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-orange-900 dark:text-orange-100 mb-2">Campaign Analytics</h3>
              <p className="text-orange-700 dark:text-orange-300 text-sm">Track the performance of your WhatsApp broadcast campaigns.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Campaigns</h4>
                <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{campaigns.length}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Sent</h4>
                <p className="text-xl sm:text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {campaigns.reduce((sum, c) => sum + c.sent, 0)}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">Delivered</h4>
                <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                  {campaigns.reduce((sum, c) => sum + c.delivered, 0)}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400">Read</h4>
                <p className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {campaigns.reduce((sum, c) => sum + c.read, 0)}
                </p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white">Recent Campaigns</h4>
              </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Campaign</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sent</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Delivered</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Read</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                  </tr>
                </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {campaigns.map(campaign => (
                      <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{campaign.name}</p>
                          </div>
                      </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(campaign.status)}`}>
                            {campaign.status}
                        </span>
                      </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{campaign.sent}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{campaign.delivered}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{campaign.read}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {campaign.scheduledDate || campaign.createdAt}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        );

      default:
        return <div>Select a tab to view content</div>;
    }
  };

                  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">WhatsApp Broadcast</h1>
        <p className="text-gray-600 dark:text-gray-400">Manage broadcast campaigns and templates</p>
      </div>

      {/* Main Container */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-4 sm:p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppBroadcast;
